import { FC } from 'react';
import { Field } from '@sitecore-jss/sitecore-jss-nextjs';

import ImageSpots, { ImageSpotsProps } from 'common/components/image-spots/ImageSpots';
import TooltipCard from 'common/components/tooltip-card/TooltipCard';
import { mapSharedPropertiesToFields } from 'common/getters';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import {
  DictionaryFields,
  ImageFields,
  SitecoreJsonImage,
  ValueFields,
} from 'common/types/sitecore';
import { Fields } from 'components/IconTiles/IconTiles';
import { ComponentProps } from 'lib/component-props';

export type Spot = {
  id: Field<string>;
  top: Field<string>;
  left: Field<string>;
  image: Field<string>;
  title: Field<string>;
  description: Field<string>;
};

export type HotspotConfig = {
  header: Field<string>;
  image: ImageFields[''];
  lightBackground: {
    boolValue: boolean;
  };
  maxHeight: Field<string>;
  backgroundCircleColor: Field<string>;
};

export type MainItemConfig = {
  fields: {
    data: {
      item: HotspotConfig;
    };
  };
};

export type HotspotProps = ComponentProps &
  Fields<'spots', ValueFields & DictionaryFields & ImageFields> &
  MainItemConfig;

const Hotspots: FC<HotspotProps> = ({ fields }) => {
  if (fields?.errors?.length) {
    return <div>{fields.errors[0].message}</div>;
  }
  if (!fields.data.item) {
    console.error('Error while loading Hotspots component. Probably problem with graphql query');
    return null;
  }

  const {
    data: {
      item: {
        spots: { results: spotResults },
        ...configResults
      },
    },
  } = fields;

  const config = configResults;
  const header = getSharedDictionary(config, 'header');

  const spots = spotResults.map((spot) => {
    return {
      ...mapSharedPropertiesToFields<Spot>(spot),
      image: Object.keys(spot.image.jsonValue.value).length === 0 ? spot.imageShared : spot.image,
    };
  });

  const imageSpots: ImageSpotsProps<{
    title: string;
    caption: string;
    image: SitecoreJsonImage;
  }>['spots'] = spots.map((spot) => {
    return {
      id: spot.id.value,
      x: spot.left.value as `${string}%`,
      y: spot.top.value as `${string}%`,
      data: {
        title: spot.title.value || '',
        caption: spot.description.value || '',
        image: spot.image || '',
      },
      renderItem: (data) => (
        <TooltipCard
          title={data.title}
          caption={data.caption}
          image={data.image}
          position={data.position}
        />
      ),
    };
  });

  return (
    <div className="container mx-auto flex flex-col items-center text-center">
      {header && header.value && (
        <h2 className="heading-2 mb-12 text-center md:mb-24">{header.value}</h2>
      )}
      <ImageSpots
        config={{
          image: {
            alt: config?.image?.jsonValue.value.alt,
            src: config?.image?.jsonValue.value.src,
          },
          maxHeight: config?.maxHeight.value,
          isLightBackground: config?.lightBackground.boolValue,
          backgroundCircleColor: config?.backgroundCircleColor.value,
        }}
        spots={imageSpots}
      />
    </div>
  );
};

export default Hotspots;
